import { useLazyQuery } from "@apollo/client";
import { DSPalette } from "@clickbank-ui/seller-design-system";
import { DSBreakpoints } from "@clickbank-ui/seller-design-system";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, Divider, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";

import { GEO_CURRENCIES } from "../../Api";
import { countryList } from "../../constants";
import { isRefundAuthFailState } from "../../util/orderHelpers";
import getRandomInt from "../../util/randomID";
import ContactInfo from "../EditInfoModal/ContactInfo";
import PaymentInfo from "../EditInfoModal/PaymentInfo";
import ShippingInfo from "../EditInfoModal/ShippingInfo";

const breakpoints = DSBreakpoints.default;
const { palette } = DSPalette;

const styles = {
	billingSameShipping: {
		fontStyle: "italic",
		fontSize: "1rem",
		marginTop: "14px"
	},
	contactInfo: {
		color: palette.text.primary,
		fontWeight: "400",
		marginLeft: "100px",

		[breakpoints.up("md")]: {
			marginLeft: "0"
		}
	},
	container: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		justifyContent: "flex-start",
		gap: "2rem",
		marginTop: "2rem"
	},
	editButton: {
		maxWidth: 126,
		minWidth: 100,
		marginTop: "16px"
	},
	section: {
		display: "flex",
		flexDirection: "column",
		minWidth: 200,

		"& .MuiTypography-root": {
			lineHeight: 1.2,

			[breakpoints.up("md")]: {
				lineHeight: 1
			}
		}
	},
	sellerAndCustomerInfoHeading: {
		margin: "24px 0",

		[breakpoints.up("md")]: {
			margin: "16px 0 0"
		}
	}
};

const CustomerInformation = ({ orderDetailsInfo }) => {
	const [acceptedCards, setAcceptedCards] = useState([]);
	const [countryCodeList, setCountryCodeList] = useState([]);
	const [buttonClicked, setButtonClicked] = useState(false);
	const [editInfoType, setEditInfoType] = useState("contactInfo");
	const [isEditModalOpen, setEditModalOpen] = useState(false);
	const [formattedPhone, setFormattedPhone] = useState("");

	const usTerritoryCodes = useMemo(() => ["GU", "PR", "VI", "AS", "MP", "MH", "PW"], []);

	const currencyCode = orderDetailsInfo?.currencyCode;
	const paymentMethod = orderDetailsInfo?.paymentMethod;
	const funnelId =
		orderDetailsInfo?.lineItems?.find(elem => elem.product.funnelId)?.product.funnelId ??
		"legacy";
	const skus = orderDetailsInfo?.lineItems?.map(elem => elem.product.itemNo);
	const vendorId =
		!!orderDetailsInfo?.lineItems && orderDetailsInfo?.lineItems.length > 0
			? orderDetailsInfo?.lineItems[0]?.product?.vendor?.nickname
			: undefined;

	const billingCustomer = orderDetailsInfo?.billingCustomer;
	const billingPhone = orderDetailsInfo?.billingCustomer?.phone;

	const shippingCustomer = orderDetailsInfo?.shippingCustomer;
	const countryCode = shippingCustomer?.country;

	useEffect(() => {
		if (billingPhone?.includes("+")) {
			setFormattedPhone(billingPhone);
		} else {
			setFormattedPhone("+" + billingPhone);
		}
	}, [billingPhone]);

	const handleEditModalClose = () => setEditModalOpen(false);

	const isProductRecurring = orderDetailsInfo?.lineItems?.some(element => element.recurring);
	const isProductActive = orderDetailsInfo?.lineItems?.some(
		element => element.paymentPlan?.activeStatus === "ACTIVE"
	);
	const hasRebills = orderDetailsInfo?.rebills?.length > 1;
	const isProductPhysical = orderDetailsInfo?.lineItems?.some(
		element => element.product?.shippable
	);

	const isShippingEditable =
		!orderDetailsInfo?.pendingOrderState &&
		orderDetailsInfo?.lineItems?.some(element => {
			if (!element.product.productType.physical) {
				return false;
			}

			// recurring can be edited for all future payments
			if (element.product.productType.recurringPhysical) {
				return element.paymentPlan.activeStatus === "ACTIVE";
			}

			// one time can be edited until it's marked as shipped by the vendor
			return element.shippingNotice === null;
		});

	const allowPaymentEdit =
		!orderDetailsInfo?.pendingOrderState &&
		((isProductRecurring && isProductActive) ||
			isRefundAuthFailState(orderDetailsInfo, null)) &&
		!["PYPL", "PAYPAL_ADAPTIVE", "PAYPAL_COMMERCE"].includes(paymentMethod);

	const isShippingSameAsBilling = () => {
		if (!isProductPhysical) {
			return false;
		}
		return (
			shippingCustomer?.address1 === billingCustomer?.address1 &&
			shippingCustomer?.address2 === billingCustomer?.address2 &&
			shippingCustomer?.city === billingCustomer?.city &&
			shippingCustomer?.state === billingCustomer?.state &&
			shippingCustomer?.zip === billingCustomer?.zip &&
			shippingCustomer?.country === billingCustomer?.country
		);
	};

	const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

	const geoCurrenciesSuccessFunction = useCallback(
		data => {
			if (!buttonClicked) return;
			///Shipping

			const geoCurrencies = data.geoCurrencies;
			const countryCodeArray = Array.from(geoCurrencies?.countries, x => x.countryCode);
			if (!countryCodeArray.includes(countryCode) && usTerritoryCodes.includes(countryCode)) {
				countryCodeArray.push(countryCode);
			}
			countryCodeArray.sort((a, b) => countryList[a]?.localeCompare(countryList[b]));
			setCountryCodeList(countryCodeArray);
			["shippingInfo", "taxInfo"].includes(editInfoType) && setEditModalOpen(true);
			///Payment
			const country = geoCurrencies?.countries.find(item => item.countryCode === countryCode);

			const acceptedCards = [];
			if (country.cardCurrencies?.includes(currencyCode)) {
				for (const entry of geoCurrencies?.acceptedPayments ?? []) {
					if (entry?.key === currencyCode) {
						acceptedCards.push(...entry.value);
						break;
					}
				}
			}

			if (acceptedCards.length === 0) {
				// no rules found; use default
				acceptedCards.push("masterCard", "visa");
				if (currencyCode === "USD") {
					acceptedCards.push("diners", "discover", "americanExpress");
				}
			}

			setAcceptedCards(Array.from(acceptedCards));
			setButtonClicked(false);
		},
		[countryCode, usTerritoryCodes, currencyCode, editInfoType, buttonClicked]
	);

	const geoErrorFunction = () => {
		// create fake geocurrencies data response with only the order's current country
		let acceptedCards = ["masterCard", "visa"];
		if (currencyCode === "USD") {
			acceptedCards.concat(["diners", "discover", "americanExpress"]);
		}
		const data = {
			geoCurrencies: {
				countries: [
					{
						// if code matches a US territory, default to US instead, and the territory will be added by geoCurrenciesSuccessFunction
						countryCode: usTerritoryCodes.includes(countryCode) ? "US" : countryCode,
						currencies: [
							{
								key: "CREDIT_CARD",
								value: [
									{
										accepted: acceptedCards,
										code: currencyCode
									}
								]
							}
						]
					}
				]
			}
		};
		geoCurrenciesSuccessFunction(data);
	};

	const [
		getGeoCurrencies,
		{ data: geoData, loading: geoLoading, called: geoCalled, error: geoError }
	] = useLazyQuery(GEO_CURRENCIES, {
		variables: { vendorId, funnelId, skus },
		context: {
			noAuth: true
		},
		onError: geoErrorFunction
	});

	useEffect(() => {
		if (!geoLoading && geoData && !geoError) {
			geoCurrenciesSuccessFunction(geoData);
		}
	}, [geoCurrenciesSuccessFunction, geoData, geoError, geoLoading]);

	const taxNo = orderDetailsInfo?.clickBankTaxNumber;

	return (
		<Box>
			<Divider />
			<Typography variant="h3" sx={styles.sellerAndCustomerInfoHeading}>
				<Trans i18nKey="OrderDetails.customerInfo">Customer Information</Trans>
			</Typography>

			<Dialog
				open={isEditModalOpen}
				maxWidth={editInfoType === "contactInfo" ? "xs" : "sm"}
				onClose={handleEditModalClose}
				aria-labelledby="edit-modal"
				aria-describedby="edit-user-info"
			>
				<>
					{editInfoType === "paymentInfoCC" || editInfoType === "paymentInfoPP" ? (
						<PaymentInfo
							infoType={editInfoType}
							handleClose={handleEditModalClose}
							orderDetailsInfo={orderDetailsInfo}
							country={countryCode}
							acceptedCards={acceptedCards}
							hasRebills={hasRebills}
							isRefundAuthFailState={isRefundAuthFailState(orderDetailsInfo, null)}
							countryCodeList={countryCodeList}
							isShippingSameAsBilling={isShippingSameAsBilling()}
							isShippable={isProductPhysical}
						/>
					) : editInfoType === "shippingInfo" ? (
						<ShippingInfo
							handleClose={handleEditModalClose}
							orderDetailsInfo={orderDetailsInfo}
							countryCodeList={countryCodeList}
							hasRebills={hasRebills}
							isShippingSameAsBilling={isShippingSameAsBilling()}
							allowBilling={allowPaymentEdit}
						/>
					) : (
						<ContactInfo
							handleClose={handleEditModalClose}
							email={billingCustomer?.email}
							phoneNumber={billingCustomer?.phone}
							receipt={orderDetailsInfo?.receiptNo}
							fullName={billingCustomer?.fullName}
							hasRebills={hasRebills}
						/>
					)}
				</>
			</Dialog>

			<Box sx={styles.container}>
				<Box sx={styles.section}>
					<Typography variant="h5" fontWeight="600" as="h4">
						<Trans i18nKey="OrderDetails.contactTitle">Contact</Trans>
					</Typography>
					<Box sx={{ marginTop: "20px", wordBreak: "break-word" }}>
						<div>
							<Typography data-cy="customer-email">
								{billingCustomer?.email}
							</Typography>
							{billingPhone && (
								<Typography data-cy="customer-phone">{formattedPhone}</Typography>
							)}
						</div>
					</Box>
					{!orderDetailsInfo?.pendingOrderState && (
						<Button
							size="small"
							color="secondary"
							id={"edit1" + getRandomInt()}
							onClick={() => {
								setButtonClicked(true);
								setEditInfoType("contactInfo");
								setEditModalOpen(true);
							}}
							sx={styles.editButton}
						>
							<Trans i18nKey="OrderDetails.editText">Edit</Trans>
						</Button>
					)}
				</Box>
				{isProductPhysical && (
					<Box sx={styles.section}>
						<Typography variant="h5" fontWeight="600" as="h4">
							<Trans i18nKey="OrderDetails.shipping">Shipping</Trans>
						</Typography>
						<Box sx={{ marginTop: "20px", wordBreak: "break-word" }}>
							<div>
								<Typography sx={{ fontWeight: "400" }} data-cy="shipping-full-name">
									{shippingCustomer?.fullName}
								</Typography>
								<Typography data-cy="shipping-address1">
									{shippingCustomer?.address1}
								</Typography>
								{shippingCustomer?.address2 && (
									<Typography data-cy="shipping-address2">
										{shippingCustomer?.address2}
									</Typography>
								)}
								<Typography>
									<span data-cy="shipping-city">{shippingCustomer?.city}</span>
									{shippingCustomer?.city && shippingCustomer?.state && ", "}
									{shippingCustomer?.state} {shippingCustomer?.zip}
								</Typography>
								<Typography>
									{shippingCustomer?.country !== "US" &&
										regionNames.of(shippingCustomer?.country)}
								</Typography>
							</div>
						</Box>
						{isShippingEditable && (
							<LoadingButton
								size="small"
								color="secondary"
								sx={styles.editButton}
								id={"editButton" + getRandomInt()}
								onClick={() => {
									setButtonClicked(true);
									setEditInfoType("shippingInfo");
									if (!geoCalled) getGeoCurrencies();
									else setEditModalOpen(true);
								}}
								loading={geoLoading && editInfoType === "shippingInfo"}
							>
								<Trans i18nKey="OrderDetails.editText">Edit</Trans>
							</LoadingButton>
						)}
					</Box>
				)}
				<Box sx={styles.section}>
					<Typography variant="h5" fontWeight="600" as="h4">
						<Trans i18nKey="OrderDetails.billing">Billing</Trans>
					</Typography>
					<Box sx={{ marginTop: "20px", wordBreak: "break-word" }}>
						<div>
							<Typography sx={{ fontWeight: "400" }} data-cy="billing-method">
								{["PYPL", "PAYPAL_ADAPTIVE", "PAYPAL_COMMERCE"].includes(
									paymentMethod
								) ? (
									<Trans i18nKey="OrderDetails.paypal">PayPal</Trans>
								) : orderDetailsInfo?.last4 ? (
									<>
										<Trans i18nKey="OrderDetails.card">
											{{
												cardType: paymentMethod.substring(0, 4)
											}}{" "}
											ending in {{ last4: orderDetailsInfo?.last4 }}
										</Trans>
										{/* Remove when Apple Pay CC payment change is allowed */}
										{paymentMethod.endsWith("APPLE") && (
											<span>&nbsp;(Apple Pay)</span>
										)}
									</>
								) : (
									paymentMethod
								)}
							</Typography>
						</div>
						{isShippingSameAsBilling() ? (
							<>
								{/* Doesn't necessarily mean the Use Shipping Address box is checked */}
								<Typography sx={styles.billingSameShipping}>
									<Trans i18nKey="OrderDetails.billingSameAsShipping">
										Billing Address same as Shipping
									</Trans>
								</Typography>
							</>
						) : (
							<Box sx={{ marginTop: "20px", wordBreak: "break-word" }}>
								<div>
									<Typography
										sx={{ fontWeight: "400" }}
										data-cy="billing-full-name"
									>
										{billingCustomer?.fullName}
									</Typography>
									<Typography data-cy="billing-address1">
										{billingCustomer?.address1}
									</Typography>
									{billingCustomer?.address2 && (
										<Typography data-cy="billing-address2">
											{billingCustomer?.address2}
										</Typography>
									)}
									<Typography>
										<span data-cy="billing-address1">
											{billingCustomer?.city}
										</span>
										{billingCustomer?.city && billingCustomer?.state && ", "}
										<span data-cy="billing-state">
											{billingCustomer?.state}
										</span>{" "}
										<span data-cy="billing-zip">{billingCustomer?.zip}</span>
									</Typography>
								</div>
							</Box>
						)}
						{isRefundAuthFailState(orderDetailsInfo, null) && (
							<Typography
								sx={{
									color: palette.error.dark,
									fontSize: "14px",
									fontWeight: "400"
								}}
							>
								<Trans i18nKey="OrderDetails.errorProcessingRefund">
									Error processing refund. Please update payment information.
								</Trans>
							</Typography>
						)}
					</Box>
					{allowPaymentEdit && (
						<LoadingButton
							size="small"
							color="secondary"
							sx={styles.editButton}
							id={"editButton2" + getRandomInt()}
							onClick={() => {
								setEditInfoType("paymentInfoCC");
								setButtonClicked(true);
								if (geoCalled) {
									acceptedCards.length > 0 && setEditModalOpen(true);
								} else getGeoCurrencies();
							}}
							loading={
								geoLoading &&
								(editInfoType === "paymentInfoCC" ||
									editInfoType === "paymentInfoPP")
							}
						>
							<Trans i18nKey="OrderDetails.editText">Edit</Trans>
						</LoadingButton>
					)}
				</Box>
				{taxNo && (
					<Box sx={styles.section}>
						<Typography variant="h5" fontWeight="600" as="h4">
							<Trans i18nKey="OrderDetails.taxInformation.header">
								Tax Information
							</Trans>
						</Typography>
						<Box sx={{ marginTop: "20px", wordBreak: "break-word" }}>
							<Typography data-cy="clickbank-vat-number">
								<Trans i18nKey="OrderDetails.taxInformation.clickBankVatNo">
									ClickBank VAT #:
								</Trans>{" "}
								{taxNo}
							</Typography>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

CustomerInformation.propTypes = {
	orderDetailsInfo: PropTypes.object
};

export default CustomerInformation;
